import React, { useState } from 'react';
import axios from 'axios';
import { css } from '@emotion/css';
import * as Sentry from '@sentry/react';
import { Grid, SxProps, useTheme, Theme, Button, Icon } from '@mui/material';
import { toast } from 'react-toastify';

import { Arpaymentmethods } from '@gql/base-types';
import { useGetPaymentMethodsBillingQuery } from '../queries/getPaymentMethodsBilling.query.generated';
import { useRooftop, useUser } from '@store';

import DefaultErrorFallback from '@components/Fallbacks/DefaultErrorFallback';
import MethodCard, { BankVerification } from '@features/billing/components/MethodCard';
import MethodAddEditModal from '@features/billing/components/MethodAddEditModal';

////////// COMPONENT //////////
const MethodTable: React.FC = () => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const sxStyles = useSxStyles(theme);

  const [modal, setModal] = useState({ open: false });
  const [methods, setMethods] = useState<Arpaymentmethods[]>([]);
  const [primaryExists, setPrimaryExists] = useState(false);
  const [customerId, setCustomerId] = React.useState<number>(0);

  const [verifications, setVerifications] = useState<Record<number, BankVerification>>({});
  // Names for the verification toasts, kept out of the check's dependencies.
  const namesRef = React.useRef<Record<number, string>>({});

  const rooftop = useRooftop();
  const user = useUser();

  React.useEffect(() => {
    if (rooftop) {
      setCustomerId(rooftop?.id);
    }
  }, [rooftop]);

  const { error, data, refetch } = useGetPaymentMethodsBillingQuery({
    variables: { customerId: customerId },
  });

  const handleModalOpen = () => {
    setModal({ ...modal, open: true });
  };
  const handleModalClose = () => {
    setModal({ ...modal, open: false });
    refetch();
  };

  const checkMethodsForPrimary = (methods: Arpaymentmethods[]) => {
    // Only a method saved through Stripe counts: an iCheck primary can't be charged, so a method
    // added while one is primary must take over as primary or autopay keeps charging the iCheck row.
    const primaryCard = methods.find(method => method.primary && method.gateway === `stripe`);
    setPrimaryExists(!!primaryCard);
  };

  React.useEffect(() => {
    const methods = data?.arpaymentmethods;
    if (methods && Array.isArray(methods) && methods.length > 0) {
      //Fix ARpaymentmethod type so it does not require customer, or add customer to quuery
      const clonedMethods = methods.map((method: Arpaymentmethods) => {
        return method;
      });
      setMethods(clonedMethods);
      checkMethodsForPrimary(clonedMethods);
      namesRef.current = Object.fromEntries(clonedMethods.map(method => [method.id, method.name]));
    } else {
      setMethods([]);
      setPrimaryExists(false);
    }
  }, [data]);

  // Bank accounts waiting on micro-deposits: ask Stripe where each stands. One that has been
  // verified is cleared server-side (and may become primary), and one Stripe will never verify
  // is retired there, so after either the list is read again.
  const pendingIds = (data?.arpaymentmethods ?? [])
    .filter(method => method.pending_setup_intent_id)
    .map(method => method.id)
    .join(',');
  // Held in a ref so a new refetch identity never re-runs the check against Stripe.
  const refetchRef = React.useRef(refetch);
  React.useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);
  React.useEffect(() => {
    if (!pendingIds || !customerId) return undefined;
    let cancelled = false;
    const check = async () => {
      try {
        const token = await user?.getToken();
        const res = await axios.post(
          '/.netlify/functions/checkStripeBankVerification',
          { customerId },
          { headers: { authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        const methods: BankVerification[] = res.data?.methods ?? [];
        // A pending row the function did not report (it could not read it, or the customer has no
        // Stripe id) is 'unchecked', never left looking as if it is still being checked.
        const byId: Record<number, BankVerification> = Object.fromEntries(methods.map(m => [m.id, m]));
        for (const id of pendingIds.split(',').map(Number)) if (!byId[id]) byId[id] = { id, status: 'unchecked' };
        setVerifications(byId);
        if (Object.values(byId).some(m => m.status === 'unchecked')) {
          toast.error('Couldn’t check a bank account’s verification with Stripe. Reload the page to try again.');
        }
        for (const m of methods) {
          const name = namesRef.current[m.id] ?? 'A bank account';
          if (m.status === 'verified') toast.success(`${name} is verified and ready to use.`);
          if (m.status === 'failed') {
            toast.error(`${name} couldn’t be verified by Stripe and was removed. Add the bank account again.`, { autoClose: false });
          }
        }
        if (methods.some(m => m.status === 'verified' || m.status === 'failed')) refetchRef.current();
      } catch (err) {
        console.error('Could not check bank verification:', err);
        Sentry.captureException(err, { tags: { feature: 'stripe-payment-method', stage: 'verify-check' } });
        if (cancelled) return;
        setVerifications(Object.fromEntries(pendingIds.split(',').map(id => [Number(id), { id: Number(id), status: 'unchecked' as const }])));
        toast.error('Couldn’t check a bank account’s verification with Stripe. Reload the page to try again.');
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [pendingIds, customerId, user]);

  if (error) {
    console.error('ERROR DISPLAYING PAYMENT METHODS', error);
    return <DefaultErrorFallback message='ERROR DISPLAYING PAYMENT METHODS' />;
  }

  return (
    <Sentry.ErrorBoundary fallback={<DefaultErrorFallback message='ERROR DISPLAYING PAYMENT METHODS' />}>
      <MethodAddEditModal
        customerId={customerId}
        open={modal.open}
        onClose={handleModalClose}
        primaryExists={primaryExists}
      />

      <div className={styles.root}>
        <Grid container spacing={2}>
          {methods.length > 0
            ? methods.map((method: Arpaymentmethods) => (
                <React.Fragment key={method.id}>
                  <Grid size={{ md: 6, xs: 12 }}>
                    <MethodCard
                      customerId={customerId}
                      method={method}
                      refetch={refetch}
                      verification={verifications[method.id]}
                    />
                  </Grid>
                </React.Fragment>
              ))
            : null}
          <React.Fragment key={0}>
            <Grid size={{ md: 6, xs: 12 }}>
              <Button
                sx={sxStyles.addButton}
                variant='outlined'
                color='primary'
                style={{ fontSize: '16px' }}
                onClick={() => handleModalOpen()}
              >
                <Icon sx={sxStyles.addIcon}>add</Icon>Add Payment Method
              </Button>
            </Grid>
          </React.Fragment>
        </Grid>
      </div>
    </Sentry.ErrorBoundary>
  );
};

export default MethodTable;

////////// STYLES //////////
const useSxStyles = (theme: Theme): Record<string, SxProps<Theme> | undefined> => ({
  addButton: {
    width: '100%',
    height: '100%',
    minHeight: '198px',
    padding: '24px',
  },
  addIcon: {
    marginTop: '-2px',
    marginRight: theme.spacing(1),
    fontSize: '16px',
  },
});

const useStyles = (theme: Theme) => {
  return {
    root: css`
      width: 100%;
      margin-top: ${theme.spacing(3)};
    `,
  };
};
